// ═══════════════════════════════════════════════════════════════════════════════
// RESPONSE ASSISTANT - Assistant pour répondre aux emails, messages, commentaires
// Colle un message → reçoit 2-3 propositions de réponses personnalisées
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, MessageSquare, Share2, Send, Sparkles, Copy, Check,
  ChevronDown, Loader2, RefreshCw, Pencil, ThumbsUp, ThumbsDown,
  Zap, User, Building2, ShoppingCart, FileText, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════════════════════
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const clientUser = localStorage.getItem('client_user');
  if (ecosystemUser) {
    try { return JSON.parse(ecosystemUser).id; } catch(e) {}
  }
  if (clientUser) {
    try { return JSON.parse(clientUser).id; } catch(e) {}
  }
  return null;
};

// Category icons
const categoryIcons = {
  clients: User,
  prospects: Zap,
  fournisseurs: ShoppingCart,
  comptabilite: FileText,
  admin: Building2,
  travaux: Building2,
  achats: ShoppingCart,
  finance: FileText,
  personnel: User,
  other: MessageSquare,
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ResponseAssistant({ 
  onClose,
  initialMessage = '',
  initialContext = 'email'
}) {
  const userId = getUserId();
  const assistantName = localStorage.getItem('assistant_custom_name') || 'Mon Assistant';
  
  // State
  const [originalMessage, setOriginalMessage] = useState(initialMessage);
  const [context, setContext] = useState(initialContext); // 'email' | 'message' | 'social'
  const [tone, setTone] = useState(null); // null = auto, 'formal', 'casual'
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [category, setCategory] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GENERATE RESPONSES
  // ═══════════════════════════════════════════════════════════════════════════
  
  const generateResponses = async () => {
    if (!originalMessage.trim()) {
      toast.error('Colle d\'abord un message à traiter');
      return;
    }
    
    if (!userId) {
      toast.error('Connecte-toi pour utiliser cette fonctionnalité');
      return;
    }
    
    setLoading(true);
    setResponses([]);
    setSelectedResponse(null);
    
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/${userId}/generate-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_message: originalMessage,
          context: context,
          tone: tone
        })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération');
      }
      
      const data = await response.json();
      
      if (data.success && data.responses?.length > 0) {
        setResponses(data.responses);
        setCategory({
          id: data.category,
          name: data.category_name,
          tone: data.detected_tone
        });
        toast.success(`${data.responses.length} réponses générées !`);
      } else {
        toast.error('Impossible de générer des réponses');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // COPY TO CLIPBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  
  const copyToClipboard = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      toast.success('Copié dans le presse-papier !');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SELECT RESPONSE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const selectResponse = (response) => {
    setSelectedResponse(response);
    setEditedContent(response.content);
    setIsEditing(false);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // LEARN FROM FEEDBACK
  // ═══════════════════════════════════════════════════════════════════════════
  
  const learnFromEdit = async () => {
    if (!selectedResponse || editedContent === selectedResponse.content) return;
    
    try {
      await fetch(`${API_URL}/api/knowledge-base/${userId}/learn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_response: selectedResponse.content,
          corrected_response: editedContent,
          context: context,
          category: category?.id || 'other'
        })
      });
      toast.success('Préférence enregistrée !');
    } catch (error) {
      console.error('Error learning:', error);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const CategoryIcon = categoryIcons[category?.id] || MessageSquare;
  
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden max-w-2xl w-full max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-white">Assistant Réponses</h2>
          <p className="text-xs text-white/70">{assistantName} t'aide à répondre</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Context selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type de contenu</label>
          <div className="flex gap-2">
            {[
              { id: 'email', label: 'Email', icon: Mail },
              { id: 'message', label: 'Message', icon: MessageSquare },
              { id: 'social', label: 'Réseaux sociaux', icon: Share2 },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setContext(opt.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                  context === opt.id
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tone selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Ton souhaité</label>
          <div className="flex gap-2">
            {[
              { id: null, label: 'Auto-détection' },
              { id: 'formal', label: 'Formel' },
              { id: 'casual', label: 'Décontracté' },
            ].map(opt => (
              <button
                key={opt.id || 'auto'}
                onClick={() => setTone(opt.id)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  tone === opt.id
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Original message input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message reçu
            <span className="text-gray-400 font-normal ml-2">(colle le message original)</span>
          </label>
          <textarea
            value={originalMessage}
            onChange={(e) => setOriginalMessage(e.target.value)}
            placeholder={
              context === 'email' 
                ? "Bonjour, je souhaite obtenir un devis pour..." 
                : context === 'social'
                ? "Super travail ! Vous intervenez dans quelle zone ?"
                : "Salut, est-ce que tu serais dispo pour..."
            }
            rows={5}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] resize-none"
          />
        </div>
        
        {/* Generate button */}
        <Button
          onClick={generateResponses}
          disabled={loading || !originalMessage.trim()}
          className="w-full bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] hover:from-[#2d5a8a] hover:to-[#4B92FF] py-3"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Génération en cours...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Générer des réponses
            </>
          )}
        </Button>
        
        {/* Category detected */}
        {category && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg"
          >
            <CategoryIcon className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700">
              Catégorie détectée : <strong>{category.name}</strong>
            </span>
            <span className="text-xs text-blue-500 ml-auto">
              Ton : {category.tone === 'formal' ? 'Formel' : 'Décontracté'}
            </span>
          </motion.div>
        )}
        
        {/* Responses */}
        <AnimatePresence>
          {responses.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Propositions de réponses
              </h3>
              
              {responses.map((response, idx) => (
                <motion.div
                  key={response.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    selectedResponse?.id === response.id
                      ? 'border-[#1E3A5F] ring-2 ring-[#1E3A5F]/20'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Response header */}
                  <div 
                    onClick={() => selectResponse(response)}
                    className="px-4 py-3 bg-gray-50 cursor-pointer flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center bg-[#1E3A5F] text-white text-xs font-bold rounded-full">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-gray-900">{response.title}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(response.content, response.id);
                        }}
                        className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedId === response.id ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                        selectedResponse?.id === response.id ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                  
                  {/* Response content (expanded) */}
                  <AnimatePresence>
                    {selectedResponse?.id === response.id && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 bg-white">
                          {isEditing ? (
                            <textarea
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                              rows={8}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 resize-none text-sm"
                              autoFocus
                            />
                          ) : (
                            <div className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                              {editedContent || response.content}
                            </div>
                          )}
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-3">
                            {isEditing ? (
                              <>
                                <Button
                                  onClick={() => {
                                    setIsEditing(false);
                                    learnFromEdit();
                                  }}
                                  size="sm"
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Valider
                                </Button>
                                <Button
                                  onClick={() => {
                                    setEditedContent(response.content);
                                    setIsEditing(false);
                                  }}
                                  variant="outline"
                                  size="sm"
                                >
                                  Annuler
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  onClick={() => setIsEditing(true)}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Pencil className="w-4 h-4 mr-1" />
                                  Modifier
                                </Button>
                                <Button
                                  onClick={() => copyToClipboard(editedContent || response.content, `final-${response.id}`)}
                                  size="sm"
                                  className="bg-[#1E3A5F] hover:bg-[#2d5a8a]"
                                >
                                  {copiedId === `final-${response.id}` ? (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Copié !
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-4 h-4 mr-1" />
                                      Copier cette réponse
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
              
              {/* Regenerate */}
              <button
                onClick={generateResponses}
                disabled={loading}
                className="w-full py-2 text-sm text-[#1E3A5F] hover:bg-[#1E3A5F]/5 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Générer d'autres propositions
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Footer tip */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          💡 Plus tu configures ta <strong>Base de Connaissances</strong>, plus les réponses seront personnalisées
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FLOATING BUTTON TO OPEN ASSISTANT
// ═══════════════════════════════════════════════════════════════════════════════

export function ResponseAssistantButton({ className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] text-white rounded-xl hover:shadow-lg transition-all ${className}`}
      >
        <Sparkles className="w-4 h-4" />
        Aide à répondre
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/60"
              onClick={() => setIsOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ResponseAssistant onClose={() => setIsOpen(false)} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
